import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import {
    CButton, CCard, CCardBody, CCardHeader, CForm, CFormInput, CFormSelect, CFormCheck, CCardFooter, CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell, CFormFeedback
} from '@coreui/react';

import { apiClient } from 'services/Api';
import DateInput from 'components/forms/DateInput';
import { jwtDecode } from 'jwt-decode';
import TextEditor from "components/forms/TextEditor";
import ToastNotification from "components/globals/ToastNotification";
import { triggerAlert } from 'components/globals/AlertNotification';

const InnovativeIdea = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState(null);
    const location = useLocation();
    const [users, setUsers] = useState([]);
    const [toast, setToast] = useState(null);
    const [isEditing, setIsEditing] = useState(true);
    const [fieldErrors, setFieldErrors] = useState({});

    const token = localStorage.getItem("access_token");
    const decodedToken = jwtDecode(token);
    const is_director = decodedToken.is_director;
    const user_id = decodedToken.user_id;

    const fetchInnovativeIdeaDetails = async () => {
        let data = null; // Declare a variable to store API response

        try {
            const response = await apiClient.get(`/innovative-ideas/${id}/`);
            data = response.data; // Store response data
            setFormData(data); // Update state
        } catch (error) {
            triggerAlert('Ideia Inovadora não foi encontrada', 'error');
            navigate("/ideias-inovadoras"); // Redirect to /ideas on error
        } finally {
            let edit = false;
            if (data) {
                if (["A Definir", "Executar Futuramente"].includes(data.state && (is_director || data.responsible === user_id))) {
                    edit = true;
                }
            }
            setIsEditing(edit); // Use the local variable, NOT formData
        }
    };

    useEffect(() => {
        if (!formData) return;

        const relevantFields = [formData.c1, formData.c2, formData.c3, formData.c4, formData.deadline, formData.alignment];
        const allEmpty = relevantFields.every((val) => val === null || val === undefined || val === '');

        if (allEmpty) return;

        const { evaluation_score, evaluation, evaluation_date } = calculateEvaluation(formData);

        setFormData((prevFormData) => ({
            ...prevFormData,
            evaluation_score,
            evaluation,
            // Set date only if not already present
            evaluation_date: prevFormData.evaluation_date || evaluation_date,
        }));
    }, [
        formData?.c1,
        formData?.c2,
        formData?.c3,
        formData?.c4,
        formData?.deadline,
        formData?.alignment
    ]);


    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await apiClient.get('/users/active/');
                setUsers(response.data);
            } catch (error) {
                console.error('Error fetching users:', error);
            }
        };

        if (!id) {
            setIsEditing(true);
            setFormData({ date: new Date().toISOString().split("T")[0], evaluation_date: '' });
        } else {
            fetchInnovativeIdeaDetails();
        }
        fetchUsers();
    }, [id, location.pathname]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        let updatedValue;
        if (type === "checkbox") {
            updatedValue = checked;
        } else if (value === "true") {
            updatedValue = true;
        } else if (value === "false") {
            updatedValue = false;
        } else {
            updatedValue = value || null;
        }

        setFormData((prevFormData) => {
            const updatedFormData = {
                ...prevFormData,
                [name]: updatedValue,
            };

            if (name === "periodicity" && updatedValue !== "Outros") {
                updatedFormData.periodicity_other = null;
            }

            return updatedFormData;
        });


        setFieldErrors((prevErrors) => {
            const updated = { ...prevErrors };
            delete updated[name];
            if (name === "periodicity" && updatedValue !== "Outros") {
                delete updated.periodicity_other;
            }
            return updated;
        });

        setIsEditing(true);
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const cleanFormData = {
                ...formData,
                evaluation_date: formData.evaluation_date || null,
            };

            if (id) {
                await apiClient.put(`/innovative-ideas/${id}/`, cleanFormData);
                triggerAlert('Ideia Inovadora atualizada com sucesso!', 'success');
                navigate(`/ideias-inovadoras`);
            } else {
                await apiClient.post('/innovative-ideas/', cleanFormData);
                triggerAlert('Ideia Inovadora atualizada com sucesso!', 'success');
                navigate('/ideias-inovadoras');
            }
        } catch (error) {
            const errors = error.response?.data;
            let errorMessage = 'Erro ao gravar';

            if (errors && typeof errors === 'object') {
                setFieldErrors(errors); // <-- Save the full error object
            } else {
                setFieldErrors({});
            }

            setTimeout(() => {
                const firstInvalidInput = document.querySelector('input.is-invalid, textarea.is-invalid, select.is-invalid');
                if (firstInvalidInput) {
                    firstInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    firstInvalidInput.focus();
                }
            }, 50);


            setToast({ title: 'Ideia Inovadora', message: errorMessage, type: 'error' });
            console.error('Error submitting form:', error);
        }
    };

    const calculateEvaluation = (updatedForm) => {
        const { c1, c2, c3, c4, deadline, alignment, evaluation_date } = updatedForm;

        const parsedC1 = parseInt(c1) || 0;
        const parsedC2 = parseInt(c2) || 0;
        const parsedC3 = parseInt(c3) || 0;
        const parsedC4 = parseInt(c4) || 0;

        // deadline logic
        const deadlineOptions = [
            "Até 6 meses",
            "6 meses a 12 meses",
            "1 a 2 anos",
            "Mais de 2 anos"
        ];
        const deadlinevalor = deadline ? (deadlineOptions.length - deadlineOptions.indexOf(deadline)) : 0;

        // Alinhamento logic
        const alignmentValue = alignment === true || alignment === "true" ? 4 : alignment === false || alignment === "false" ? 1 : 0;

        const total = parsedC1 + parsedC2 + parsedC3 + parsedC4 + deadlinevalor + alignmentValue;
        const maxScore = 24; // 6 categories * 4 points each

        const percentScore = parseFloat(((total / maxScore) * 100).toFixed(2));
        const evaluation = percentScore >= 65 ? "Aprovada" : "Excluída";

        return {
            evaluation_score: percentScore,
            evaluation,
            evaluation_date: evaluation_date || (new Date().toISOString().split("T")[0]),
        };
    };


    if (formData !== null) {
        if (!isEditing && (formData.status === "Executar no Imediato" || (user_id !== formData.responsible && !is_director))) {
            const renderText = (text) => {
                return <div dangerouslySetInnerHTML={{ __html: text || "-" }} />;
            };

            const getLabel = (value) => {
                const labels = {
                    1: "Muito Baixo",
                    2: "Baixo",
                    3: "Elevado",
                    4: "Muito Elevado"
                };
                return labels[value] || "";
            };

            return (
                <div className="container mt-4">
                    <h4 className="text-center mb-5">Detalhes da Ideia Inovadora</h4>
                    {[
                        { label: "Código", value: formData.code },
                        { label: "Designação da Ideia", value: formData.designation },
                        { label: "Descrição da Ideia", value: renderText(formData.description) },
                        { label: "Descrição do impacto", value: renderText(formData.impact) },
                        { label: "Origem", value: formData.origin },
                        { label: "Data", value: formData.date },
                        {
                            label: "Natureza",
                            value: (
                                <>
                                    {formData.organizational && <div>Organizacional</div>}
                                    {formData.service && <div>Serviço</div>}
                                    {formData.marketing && <div>Marketing</div>}
                                    {formData.process && <div>Processo</div>}
                                    {formData.product && <div>Produto</div>}
                                </>
                            )
                        },
                        { label: "Tipo de ideia", value: formData.idea_type },
                        {
                            label: "Avaliação",
                            value: (
                                <CTable striped bordered hover>
                                    <CTableHead>
                                        <CTableRow>
                                            <CTableHeaderCell className="text-center col-md-2">Viabilidade</CTableHeaderCell>
                                            <CTableHeaderCell className="text-center col-md-2">Recursos</CTableHeaderCell>
                                            <CTableHeaderCell className="text-center col-md-1">Retorno</CTableHeaderCell>
                                            <CTableHeaderCell className="text-center col-md-1">Risco</CTableHeaderCell>
                                            <CTableHeaderCell className="text-center col-md-1">Prazo</CTableHeaderCell>
                                            <CTableHeaderCell className="text-center col-md-1">Alinhamento</CTableHeaderCell>
                                            <CTableHeaderCell className="text-center col-md-2" colSpan={2}>Avaliação</CTableHeaderCell>
                                        </CTableRow>
                                    </CTableHead>
                                    <CTableBody>
                                        <CTableRow>
                                            <CTableDataCell className="text-center">{getLabel(formData.c1)}</CTableDataCell>
                                            <CTableDataCell className="text-center">{getLabel(formData.c2)}</CTableDataCell>
                                            <CTableDataCell className="text-center">{getLabel(formData.c3)}</CTableDataCell>
                                            <CTableDataCell className="text-center">{getLabel(formData.c4)}</CTableDataCell>
                                            <CTableDataCell className="text-center">{formData.deadline}</CTableDataCell>
                                            <CTableDataCell className="text-center">{formData.alignment === true ? "Sim" : formData.alignment === false ? "Não" : ""}</CTableDataCell>
                                            <CTableDataCell className="text-center">{formData.evaluation_score}</CTableDataCell>
                                            <CTableDataCell className="text-center">{formData.evaluation}</CTableDataCell>
                                        </CTableRow>
                                    </CTableBody>
                                </CTable>
                            )
                        },
                        { label: "Data da Avaliação", value: formData.evaluation_date },
                        { label: "Gestor", value: formData.responsible_name },
                        ...(formData.idea_type === "Projeto IDI" ? [
                            { label: "Riscos/Limitações", value: renderText(formData.risks) },
                            { label: "Ações para minimizar riscos", value: renderText(formData.actions) },
                            {
                                label: "Periodicidade de Avaliação de Riscos",
                                value: formData.periodicity === "Outros" && formData.periodicity_other
                                    ? `Outros - ${formData.periodicity_other}`
                                    : formData.periodicity,
                            },
                            { label: "Estado de arte", value: renderText(formData.state_of_art) },
                            { label: "Orçamento", value: formData.budget },
                            { label: "Tipo de projeto", value: formData.project_type },
                            { label: "Justificação", value: renderText(formData.justification) },
                        ] : []),

                        { label: "Decisão", value: formData.state },
                        { label: "Observações", value: renderText(formData.obs) },
                    ].map((item, index) => (
                        <CCard key={index} className="mb-3">
                            <CCardHeader>
                                <h5>{item.label}</h5>
                            </CCardHeader>
                            <CCardBody>
                                <div>{item.value}</div>
                            </CCardBody>
                        </CCard>
                    ))}

                    {/* Back button */}
                    <div className="d-flex justify-content-end gap-2 mt-3 mb-3">
                        <CButton color="danger" className="text-white" onClick={() => navigate('/ideias')}>
                            Voltar
                        </CButton>
                    </div>
                </div>
            );
        }

        return (
            <div className="container mt-4">
                {/* Error Toast - Appears on submit failure */}
                <ToastNotification toast={toast} setToast={setToast} />

                <CCard>
                    <CCardHeader>
                        <h4>{id ? 'Editar Ideia Inovadora' : 'Nova Ideia Inovadora'}</h4>
                    </CCardHeader>
                    <CCardBody>
                        <CForm onSubmit={handleSubmit}>
                            {/* Show "Código" field only when editing */}
                            {id && (
                                <CFormInput
                                    type="text"
                                    label="Código"
                                    name="code"
                                    value={formData.code}
                                    readOnly
                                    className="mb-3"
                                />
                            )}

                            <div className="mb-3">
                                <CFormInput
                                    type="text"
                                    label={<strong>Designação da Ideia</strong>}
                                    name="designation"
                                    value={formData.designation ?? ''}
                                    onChange={handleInputChange}
                                    invalid={!!fieldErrors.designation}
                                />
                                {fieldErrors.designation && (
                                    <CFormFeedback invalid>{fieldErrors.designation[0]}</CFormFeedback>
                                )}
                            </div>

                            <TextEditor
                                label="Descrição da Ideia"
                                value={formData.description ?? ''}
                                onChange={(value) =>
                                    handleInputChange({ target: { name: "description", value } })
                                }
                                className="mb-3"
                            />

                            <TextEditor
                                label="Descrição do impacto"
                                value={formData.impact ?? ''}
                                onChange={(value) =>
                                    handleInputChange({ target: { name: "impact", value } })
                                }
                                className="mb-3"
                            />

                            <div className="mb-3">
                                <CFormSelect
                                    label={<strong>Origem</strong>}
                                    name="origin"
                                    value={formData.origin ?? ''}
                                    onChange={handleInputChange}
                                    invalid={!!fieldErrors.origin}
                                >
                                    <option value="">Selecione</option>
                                    {["Sugestão/Ideias", "Revisão Gestão", "Vigilâncias", "Outros"].map((origin, index) => (
                                        <option key={index} value={origin}>{origin}</option>
                                    ))}
                                </CFormSelect>
                                {fieldErrors.origin && (
                                    <CFormFeedback invalid>{fieldErrors.origin[0]}</CFormFeedback>
                                )}
                            </div>

                            <DateInput
                                label={<strong>Data</strong>}
                                name="date"
                                value={formData.date ?? ''}
                                onChange={handleInputChange}
                            />

                            <div className="mt-3 mb-3">
                                <div><strong>Natureza</strong></div>
                                <CFormCheck
                                    label="Organizacional"
                                    name="organizational"
                                    checked={formData.organizational}
                                    onChange={handleInputChange}
                                />
                                <CFormCheck
                                    label="Serviço"
                                    name="service"
                                    checked={formData.service}
                                    onChange={handleInputChange}
                                />
                                <CFormCheck
                                    label="Marketing"
                                    name="marketing"
                                    checked={formData.marketing}
                                    onChange={handleInputChange}
                                />
                                <CFormCheck
                                    label="Processo"
                                    name="process"
                                    checked={formData.process}
                                    onChange={handleInputChange}
                                />
                                <CFormCheck
                                    label="Produto"
                                    name="product"
                                    checked={formData.product}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className="mb-3">
                                <CFormSelect
                                    label={<strong>Tipo de ideia</strong>}
                                    name="idea_type"
                                    value={formData.idea_type ?? ''}
                                    onChange={handleInputChange}
                                    invalid={!!fieldErrors.idea_type}
                                >
                                    <option value="">Selecione</option>
                                    {["Ação de inovação", "Projeto IDI"].map((idea_type, index) => (
                                        <option key={index} value={idea_type}>{idea_type}</option>
                                    ))}
                                </CFormSelect>
                                {fieldErrors.idea_type && (
                                    <CFormFeedback invalid>{fieldErrors.idea_type[0]}</CFormFeedback>
                                )}
                            </div>

                            {is_director && (
                                <>
                                    <CTable striped bordered hover>
                                        <CTableHead>
                                            <CTableRow>
                                                <CTableHeaderCell className="text-center col-md-2">Viabilidade</CTableHeaderCell>
                                                <CTableHeaderCell className="text-center col-md-2">Recursos</CTableHeaderCell>
                                                <CTableHeaderCell className="text-center col-md-1">Retorno</CTableHeaderCell>
                                                <CTableHeaderCell className="text-center col-md-1">Risco</CTableHeaderCell>
                                                <CTableHeaderCell className="text-center col-md-1">Prazo</CTableHeaderCell>
                                                <CTableHeaderCell className="text-center col-md-1">Alinhamento</CTableHeaderCell>
                                                <CTableHeaderCell className="text-center col-md-2" colSpan={2}>Avaliação</CTableHeaderCell>
                                            </CTableRow>
                                        </CTableHead>
                                        <CTableBody>
                                            <CTableRow>
                                                <CTableDataCell>
                                                    <CFormSelect name="c1" value={formData.c1} onChange={handleInputChange}>
                                                        <option value="">Selecione...</option>
                                                        <option value="1">Muito Baixo</option>
                                                        <option value="2">Baixo</option>
                                                        <option value="3">Elevado</option>
                                                        <option value="4">Muito Elevado</option>
                                                    </CFormSelect>
                                                </CTableDataCell>
                                                <CTableDataCell>
                                                    <CFormSelect name="c2" value={formData.c2} onChange={handleInputChange}>
                                                        <option value="">Selecione...</option>
                                                        <option value="4">Muito Baixo</option>
                                                        <option value="3">Baixo</option>
                                                        <option value="2">Elevado</option>
                                                        <option value="1">Muito Elevado</option>
                                                    </CFormSelect>
                                                </CTableDataCell>
                                                <CTableDataCell>
                                                    <CFormSelect name="c3" value={formData.c3} onChange={handleInputChange}>
                                                        <option value="">Selecione...</option>
                                                        <option value="1">Muito Baixo</option>
                                                        <option value="2">Baixo</option>
                                                        <option value="3">Elevado</option>
                                                        <option value="4">Muito Elevado</option>
                                                    </CFormSelect>
                                                </CTableDataCell>
                                                <CTableDataCell>
                                                    <CFormSelect name="c4" value={formData.c4} onChange={handleInputChange}>
                                                        <option value="">Selecione...</option>
                                                        <option value="4">Muito Baixo</option>
                                                        <option value="3">Baixo</option>
                                                        <option value="2">Elevado</option>
                                                        <option value="1">Muito Elevado</option>
                                                    </CFormSelect>
                                                </CTableDataCell>
                                                <CTableDataCell>
                                                    <CFormSelect name="deadline" value={formData.deadline} onChange={handleInputChange}>
                                                        <option value="">Selecione...</option>
                                                        <option value="Até 6 meses">Até 6 meses</option>
                                                        <option value="6 meses a 12 meses">6 meses a 12 meses</option>
                                                        <option value="1 a 2 anos">1 a 2 anos</option>
                                                        <option value="Mais de 2 anos">Mais de 2 anos</option>
                                                    </CFormSelect>
                                                </CTableDataCell>
                                                <CTableDataCell>
                                                    <CFormSelect name="alignment" value={formData.alignment} onChange={handleInputChange}>
                                                        <option value="">Selecione...</option>
                                                        <option value="true">Sim</option>
                                                        <option value="false">Não</option>
                                                    </CFormSelect>
                                                </CTableDataCell>
                                                <CTableDataCell>
                                                    <CFormInput name="evaluation_score" value={formData.evaluation_score ?? ''} readOnly />
                                                </CTableDataCell>
                                                <CTableDataCell>
                                                    <CFormInput name="evaluation" value={formData.evaluation ?? ''} readOnly />
                                                </CTableDataCell>
                                            </CTableRow>
                                        </CTableBody>
                                    </CTable>

                                    <DateInput
                                        label={<strong>Data da Avaliação</strong>}
                                        name="evaluation_date"
                                        value={formData.evaluation_date ?? ''}
                                        onChange={handleInputChange}
                                    />

                                    <CFormSelect
                                        label={<strong>Gestor</strong>}
                                        name="responsible"
                                        value={formData.responsible || ""}
                                        onChange={handleInputChange}
                                        className="mb-3"
                                    >
                                        <option value="">Selecione um colaborador</option>
                                        {users.map((user) => (
                                            <option key={user.id} value={user.id}>
                                                {user.first_name} {user.last_name}
                                            </option>
                                        ))}
                                    </CFormSelect>
                                </>
                            )}

                            {formData.idea_type === "Projeto IDI" && (is_director || formData.responsible === user_id) && (
                                <>
                                    <TextEditor
                                        label="Riscos/Limitações"
                                        value={formData.risks ?? ''}
                                        onChange={(value) =>
                                            handleInputChange({ target: { name: "risks", value } })
                                        }
                                        className="mb-3"
                                    />

                                    <TextEditor
                                        label="Ações para minimizar riscos"
                                        value={formData.actions ?? ''}
                                        onChange={(value) =>
                                            handleInputChange({ target: { name: "actions", value } })
                                        }
                                        className="mb-3"
                                    />

                                    <div className="mb-3">
                                        <CFormSelect
                                            label={<strong>Periodicidade de Avaliação de Riscos</strong>}
                                            name="periodicity"
                                            value={formData.periodicity ?? ''}
                                            onChange={handleInputChange}
                                            invalid={!!fieldErrors.periodicity}
                                        >
                                            <option value="">Selecione</option>
                                            {["Trimestral", "Semestral", "Outros"].map((periodicity, index) => (
                                                <option key={index} value={periodicity}>{periodicity}</option>
                                            ))}
                                        </CFormSelect>
                                        {fieldErrors.periodicity && (
                                            <CFormFeedback invalid>{fieldErrors.periodicity[0]}</CFormFeedback>
                                        )}
                                    </div>

                                    {formData.periodicity === "Outros" && (
                                        <div className="mb-3">
                                            <CFormInput
                                                type="text"
                                                label={<strong>Periodicidade (Outros)</strong>}
                                                name="periodicity_other"
                                                value={formData.periodicity_other ?? ''}
                                                onChange={handleInputChange}
                                                invalid={!!fieldErrors.periodicity_other}
                                            />
                                            {fieldErrors.periodicity_other && (
                                                <CFormFeedback invalid>{fieldErrors.periodicity_other[0]}</CFormFeedback>
                                            )}
                                        </div>
                                    )}

                                    <TextEditor
                                        label="Estado de arte"
                                        value={formData.state_of_art ?? ''}
                                        onChange={(value) =>
                                            handleInputChange({ target: { name: "state_of_art", value } })
                                        }
                                        className="mb-3"
                                    />

                                    <CFormInput
                                        type="number"
                                        step={0.01}
                                        label={<strong>Orçamento</strong>}
                                        name="budget"
                                        value={formData.budget ?? ''}
                                        onChange={handleInputChange}
                                        className="mb-3"
                                    />

                                    <CFormSelect
                                        label={<strong>Tipo de projeto</strong>}
                                        name="project_type"
                                        value={formData.project_type ?? ''}
                                        onChange={handleInputChange}
                                        className="mb-3"
                                    >
                                        <option value="">Selecione</option>
                                        {["Projeto I&D", "Projeto I"].map((project_type, index) => (
                                            <option key={index} value={project_type}>{project_type}</option>
                                        ))}
                                    </CFormSelect>

                                    <TextEditor
                                        label="Justificação"
                                        value={formData.justification ?? ''}
                                        onChange={(value) =>
                                            handleInputChange({ target: { name: "justification", value } })
                                        }
                                        className="mb-3"
                                    />
                                </>
                            )}

                            {is_director && (
                                <>
                                    <CFormSelect
                                        label={<strong>Decisão</strong>}
                                        name="state"
                                        value={formData.state ?? 'A Definir'}
                                        onChange={handleInputChange}
                                        className="mb-3"
                                    >
                                        {["A Definir", "Executar no Imediato", "Executar Futuramente", "Descartar"].map((state, index) => (
                                            <option key={index} value={state}>{state}</option>
                                        ))}
                                    </CFormSelect>

                                    <TextEditor
                                        label="Observações"
                                        value={formData.obs ?? ''}
                                        onChange={(value) =>
                                            handleInputChange({ target: { name: "obs", value } })
                                        }
                                        className="mb-3"
                                    />
                                </>
                            )}
                        </CForm>
                    </CCardBody>
                    <CCardFooter className="d-flex justify-content-end gap-2">
                        <CButton color="danger" className="text-white" onClick={() => navigate("/ideias-inovadoras")}>
                            Cancelar
                        </CButton>
                        <CButton color="success" className="text-white" type="submit" onClick={handleSubmit}>
                            Confirmar
                        </CButton>
                    </CCardFooter>
                </CCard>
            </div>
        );
    }

    return (
        <div className="container mt-4">
            <h2>Loading...</h2>
        </div>
    );
};

export default InnovativeIdea;
